/**
 * BlockRenderer - Unified entry point for rendering all block types
 *
 * This component:
 * 1. Gets the appropriate visual component for the block type from the registry
 * 2. Wraps it with behavior components based on the current render mode
 * 3. Renders the final composed component
 *
 * ## The Three Modes:
 *
 * - **editor**: Wraps visual with Selectable + Draggable for full interactivity
 * - **static**: Renders visual directly with no wrappers (thumbnails, previews)
 * - **animated**: Wraps visual with AnimatedPosition for timeline-driven playback
 */

import React from 'react'
import { useBlockRenderContext } from './BlockRenderContext'
import { getVisualForBlockType } from './Registry'
import type { PlayBlock_Union, PlayBlockType } from '@/types/blocks'
import { Selectable } from './behaviors/editor/Selectable'
import { Draggable } from './behaviors/editor/Draggable'
import { AnimatedPosition } from '@/components/blocks/behaviors'
import { BlockErrorBoundary } from './BlockErrorBoundary'

interface BlockRendererProps {
  block: PlayBlock_Union
}

/**
 * Unified block renderer that handles all block types across all render modes
 *
 * @example Editor mode
 * ```tsx
 * <BlockRenderProvider value={{ mode: 'editor', ... }}>
 *   <BlockRenderer block={playerBlock} />
 * </BlockRenderProvider>
 * ```
 *
 * @example Static mode (thumbnails)
 * ```tsx
 * <BlockRenderProvider value={{ mode: 'static', ... }}>
 *   <BlockRenderer block={playBlock} />
 * </BlockRenderProvider>
 * ```
 *
 * @example Animated mode
 * ```tsx
 * <BlockRenderProvider value={{ mode: 'animated', animationTime: 0.5, ... }}>
 *   <BlockRenderer block={playerBlock} />
 * </BlockRenderProvider>
 * ```
 */
export function BlockRenderer({ block }: BlockRendererProps) {
  const context = useBlockRenderContext()

  // Get the pure visual component for this block type
  const Visual = getVisualForBlockType(block.type as PlayBlockType)

  // Build the visual element (shared across all modes)
  const visual = <Visual block={block} />

  // Render based on mode, wrapped in error boundary
  let content: React.ReactElement

  switch (context.mode) {
    case 'editor':
      content = (
        <Selectable blockId={String(block.id)}>
          <Draggable blockId={String(block.id)}>
            {visual}
          </Draggable>
        </Selectable>
      )
      break

    case 'static':
      // Static mode: pure visual, no wrappers
      content = visual
      break

    case 'animated':
      content = (
        <AnimatedPosition blockId={String(block.id)} block={block}>
          {visual}
        </AnimatedPosition>
      )
      break

    default: {
      // Exhaustive check: TypeScript enforces all RenderMode values are handled
      const _exhaustiveCheck: never = context.mode
      return _exhaustiveCheck
    }
  }

  return (
    <BlockErrorBoundary blockType={block.type} blockId={String(block.id)}>
      {content}
    </BlockErrorBoundary>
  )
}
